import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ChartData } from '../pages/home/financial';

declare module 'jspdf' {
	interface jsPDF {
		lastAutoTable?: {
			finalY: number;
		};
		autoTable: (options: any) => void;
	}
}

interface PDFData {
	date: string;
	monitorings: number;
	maintenances: number;
	monitoringValue: number;
	maintenanceValue: number;
	dailyTotal: number;
}

interface PDFOptions {
	dateInitial: Date;
	dateFinal: Date;
	clientName?: string;
	totalIncome: number;
	logoPath?: string;
	signaturePath?: string;
	monitoringUnitValue: number;
	maintenanceUnitValue: number;
}

const loadImageAsBase64 = async (url: string): Promise<string> => {
	try {
		if (url.startsWith('data:')) {
			return url;
		}

		if (url.endsWith('.svg')) {
			const response = await fetch(url);
			const svgText = await response.text();
			return `data:image/svg+xml;base64,${btoa(svgText)}`;
		}

		const response = await fetch(url);
		const blob = await response.blob();
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onloadend = () => resolve(reader.result as string);
			reader.onerror = reject;
			reader.readAsDataURL(blob);
		});
	} catch (error) {
		console.error('Erro ao carregar imagem:', error);
		throw error;
	}
};

export const generateFinancialPDF = async (
	data: PDFData[],
	options: PDFOptions
) => {
	const doc = new jsPDF();
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();

	const primaryColor = [41, 128, 185];
	const secondaryColor = [52, 152, 219];
	const grayColor = [149, 165, 166];

	doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
	doc.rect(0, 0, pageWidth, 70, 'F');

	let logoAdded = false;
	let yPosition = 35;

	if (options.logoPath) {
		try {
			const logoData = await loadImageAsBase64(options.logoPath);
			const logoWidth = 70;
			const logoHeight = logoWidth * (32 / 135);
			const logoX = (pageWidth - logoWidth) / 2;
			const logoY = 10;

			doc.addImage(logoData, 'SVG', logoX, logoY, logoWidth, logoHeight);
			logoAdded = true;
			yPosition = logoY + logoHeight + 12;
		} catch (error) {
			logoAdded = false;
			yPosition = 25;
		}
	}

	doc.setTextColor(255, 255, 255);
	doc.setFontSize(16);
	doc.setFont('helvetica', 'bold');
	doc.text('RELATÓRIO FINANCEIRO', pageWidth / 2, yPosition, {
		align: 'center',
	});

	doc.setFontSize(10);
	doc.setFont('helvetica', 'normal');
	doc.text(
		'Relatório de Monitoramentos e Manutenções',
		pageWidth / 2,
		yPosition + 8,
		{ align: 'center' }
	);

	doc.setFontSize(8);
	const periodText = `Período: ${options.dateInitial.toLocaleDateString('pt-BR')} à ${options.dateFinal.toLocaleDateString('pt-BR')}`;
	doc.text(periodText, pageWidth / 2, yPosition + 16, { align: 'center' });

	if (options.clientName) {
		doc.setFontSize(9);
		doc.setFont('helvetica', 'bold');
		doc.text(
			`Cliente: ${options.clientName}`,
			pageWidth / 2,
			yPosition + 24,
			{ align: 'center' }
		);
	}

	yPosition += 40;

	doc.setFillColor(secondaryColor[0], secondaryColor[1], secondaryColor[2]);
	doc.rect(20, yPosition - 8, pageWidth - 40, 18, 'F');

	doc.setTextColor(255, 255, 255);
	doc.setFontSize(12);
	doc.setFont('helvetica', 'bold');
	doc.text('RESUMO FINANCEIRO', pageWidth / 2, yPosition, {
		align: 'center',
	});

	doc.setFontSize(14);
	doc.text(
		`Total: R$ ${options.totalIncome.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
		pageWidth / 2,
		yPosition + 7,
		{ align: 'center' }
	);

	yPosition += 25;

	const tableData = data.map((item) => [
		item.date,
		item.monitorings.toString(),
		item.maintenances.toString(),
		`R$ ${item.monitoringValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
		`R$ ${item.maintenanceValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
		`R$ ${item.dailyTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
	]);

	const tableHeaders = [
		'DATA',
		'QTD. MONIT.',
		'QTD. MANUT.',
		'VALOR MONITORAMENTO',
		'VALOR MANUTENÇÃO',
		'TOTAL DIÁRIO',
	];

	const columnWidths = [22, 18, 18, 32, 28, 28];
	const totalTableWidth = columnWidths.reduce((sum, width) => sum + width, 0);
	const tableMargin = (pageWidth - totalTableWidth) / 2;

	let finalYAfterTable = yPosition;

	autoTable(doc, {
		startY: yPosition,
		head: [tableHeaders],
		body: tableData,
		theme: 'grid',
		styles: {
			fontSize: 8,
			cellPadding: 3,
			textColor: [0, 0, 0],
			lineColor: [0, 0, 0],
			lineWidth: 0.1,
		},
		headStyles: {
			fillColor: [primaryColor[0], primaryColor[1], primaryColor[2]],
			textColor: [255, 255, 255],
			fontStyle: 'bold',
			lineWidth: 0.1,
			fontSize: 8,
		},
		alternateRowStyles: {
			fillColor: [245, 245, 245],
		},
		margin: { left: tableMargin, right: tableMargin },
		tableWidth: totalTableWidth,
		columnStyles: {
			0: { cellWidth: columnWidths[0] },
			1: { cellWidth: columnWidths[1] },
			2: { cellWidth: columnWidths[2] },
			3: { cellWidth: columnWidths[3] },
			4: { cellWidth: columnWidths[4] },
			5: { cellWidth: columnWidths[5] },
		},
		didDrawPage: (data: any) => {
			if (data.pageNumber === doc.getNumberOfPages()) {
				finalYAfterTable = data.cursor.y;
			}
		},
	});

	const signatureSectionHeight = 100;
	let currentY = finalYAfterTable + 20;

	if (currentY + signatureSectionHeight > pageHeight - 20) {
		doc.addPage();
		currentY = 20;
	}

	const totalMonitorings = data.reduce(
		(sum, item) => sum + item.monitorings,
		0
	);
	const totalMaintenances = data.reduce(
		(sum, item) => sum + item.maintenances,
		0
	);
	const totalMonitoringValue = data.reduce(
		(sum, item) => sum + item.monitoringValue,
		0
	);
	const totalMaintenanceValue = data.reduce(
		(sum, item) => sum + item.maintenanceValue,
		0
	);

	const cardWidth = pageWidth - 60;
	const cardX = (pageWidth - cardWidth) / 2;

	doc.setFillColor(245, 245, 245);
	doc.rect(cardX, currentY, cardWidth, 28, 'F');

	doc.setTextColor(0, 0, 0);
	doc.setFontSize(9);
	doc.setFont('helvetica', 'bold');
	doc.text('TOTAIS CONSOLIDADOS:', cardX + 10, currentY + 7);

	doc.setFontSize(8);
	doc.setFont('helvetica', 'normal');

	doc.text(`Monitoramentos: ${totalMonitorings}`, cardX + 10, currentY + 15);
	doc.text(
		`R$ ${totalMonitoringValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
		cardX + cardWidth - 10,
		currentY + 15,
		{ align: 'right' }
	);

	doc.text(`Manutenções: ${totalMaintenances}`, cardX + 10, currentY + 22);
	doc.text(
		`R$ ${totalMaintenanceValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
		cardX + cardWidth - 10,
		currentY + 22,
		{ align: 'right' }
	);

	currentY += 38;

	const signatureHeight = 60;
	if (currentY + signatureHeight > pageHeight - 20) {
		doc.addPage();
		currentY = 20;
	}

	doc.setDrawColor(grayColor[0], grayColor[1], grayColor[2]);
	doc.setLineWidth(0.5);

	const signatureAreaY = currentY + 10;

	if (options.signaturePath) {
		try {
			const signatureData = await loadImageAsBase64(
				options.signaturePath
			);
			const signatureWidth = 80;
			const signatureHeight = 30;
			const signatureX = (pageWidth - signatureWidth) / 2;

			if (signatureAreaY + signatureHeight + 25 < pageHeight - 20) {
				doc.addImage(
					signatureData,
					'PNG',
					signatureX,
					signatureAreaY,
					signatureWidth,
					signatureHeight
				);
				doc.line(
					50,
					signatureAreaY + signatureHeight + 5,
					pageWidth - 50,
					signatureAreaY + signatureHeight + 5
				);
				doc.setTextColor(0, 0, 0);
				doc.setFontSize(10);
				doc.setFont('helvetica', 'normal');
				doc.text(
					'Assinatura do Proprietário/Responsável',
					pageWidth / 2,
					signatureAreaY + signatureHeight + 15,
					{ align: 'center' }
				);
			} else {
				doc.addPage();
				const newSignatureY = 20;
				doc.addImage(
					signatureData,
					'PNG',
					signatureX,
					newSignatureY,
					signatureWidth,
					signatureHeight
				);
				doc.line(
					50,
					newSignatureY + signatureHeight + 5,
					pageWidth - 50,
					newSignatureY + signatureHeight + 5
				);
				doc.setTextColor(0, 0, 0);
				doc.setFontSize(10);
				doc.setFont('helvetica', 'normal');
				doc.text(
					'Assinatura do Proprietário/Responsável',
					pageWidth / 2,
					newSignatureY + signatureHeight + 15,
					{ align: 'center' }
				);
			}
		} catch (error) {
			if (signatureAreaY + 40 > pageHeight - 20) {
				doc.addPage();
				currentY = 20;
			}
			doc.line(50, currentY + 25, pageWidth - 50, currentY + 25);
			doc.setTextColor(0, 0, 0);
			doc.setFontSize(10);
			doc.setFont('helvetica', 'normal');
			doc.text(
				'Assinatura do Proprietário/Responsável',
				pageWidth / 2,
				currentY + 40,
				{ align: 'center' }
			);
		}
	} else {
		if (signatureAreaY + 40 > pageHeight - 20) {
			doc.addPage();
			currentY = 20;
		}
		doc.line(50, currentY + 25, pageWidth - 50, currentY + 25);
		doc.setTextColor(0, 0, 0);
		doc.setFontSize(10);
		doc.setFont('helvetica', 'normal');
		doc.text(
			'Assinatura do Proprietário/Responsável',
			pageWidth / 2,
			currentY + 40,
			{ align: 'center' }
		);
	}

	const totalPages = doc.getNumberOfPages();

	for (let i = 1; i <= totalPages; i++) {
		doc.setPage(i);

		doc.setFontSize(8);
		doc.setTextColor(grayColor[0], grayColor[1], grayColor[2]);
		doc.text(
			`Página ${i} de ${totalPages}`,
			pageWidth / 2,
			pageHeight - 15,
			{ align: 'center' }
		);

		if (i === totalPages) {
			const currentDate = new Date().toLocaleDateString('pt-BR');
			const currentTime = new Date().toLocaleTimeString('pt-BR', {
				hour: '2-digit',
				minute: '2-digit',
			});

			doc.text(
				`Emitido em ${currentDate} às ${currentTime}`,
				20,
				pageHeight - 5
			);
			doc.text(
				'ReeferSys - Todos os direitos reservados',
				pageWidth - 20,
				pageHeight - 5,
				{ align: 'right' }
			);
		}
	}

	doc.setPage(1);

	return doc;
};

export const preparePDFData = (
	chartData: ChartData[],
	monitoringUnitValue: number,
	maintenanceUnitValue: number
): PDFData[] => {
	return chartData.map((item) => {
		const monitoringValue = item.monitorings * monitoringUnitValue;
		const maintenanceValue = item.maintenances * maintenanceUnitValue + item.totalMateriais;
		const dailyTotal = monitoringValue + maintenanceValue;

		return {
			date: new Date(item.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }),
			monitorings: item.monitorings,
			maintenances: item.maintenances,
			monitoringValue,
			maintenanceValue,
			dailyTotal,
		};
	});
};
